import { useCallback, useEffect, useRef } from 'react';
import { useAppStore } from '@/stores/app-store';
import { runSimulationTurn, mapEventsToLayers } from '@/services/simulation-engine';

// ---------------------------------------------------------------------------
// useSimulation — orchestrates Claude streaming + store updates
// ---------------------------------------------------------------------------

export function useSimulation() {
  const simulation = useAppStore((s) => s.simulation);
  const countryProfiles = useAppStore((s) => s.countryProfiles);
  const setSimStatus = useAppStore((s) => s.setSimStatus);
  const setStreamingText = useAppStore((s) => s.setStreamingText);
  const pushSimulationTurn = useAppStore((s) => s.pushSimulationTurn);
  const setSimMissiles = useAppStore((s) => s.setSimMissiles);
  const setSimTroopMovements = useAppStore((s) => s.setSimTroopMovements);
  const setSimConflictZones = useAppStore((s) => s.setSimConflictZones);
  const setAutoMode = useAppStore((s) => s.setAutoMode);
  const setViewState = useAppStore((s) => s.setViewState);

  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef(false);

  // Run a single turn
  const runTurn = useCallback(async (choiceId?: 'A' | 'B' | 'C') => {
    if (!simulation.scenario) return;
    abortRef.current = false;

    setSimStatus('streaming');
    setStreamingText('');

    let accumulated = '';

    try {
      const turn = await runSimulationTurn(
        simulation.scenario,
        countryProfiles,
        simulation.turns,
        choiceId,
        (chunk) => {
          if (abortRef.current) return;
          accumulated += chunk;
          setStreamingText(accumulated);
        },
      );

      if (abortRef.current) return;

      // Parse map_events → overlay layers
      const layers = mapEventsToLayers(turn.map_events, turn.turnNumber);
      setSimMissiles(layers.missiles);
      setSimTroopMovements(layers.troopMovements);
      setSimConflictZones(layers.conflictZones);

      // Auto-pan map to the first event with coordinates
      const focusEvent = turn.map_events.find(
        (e) => 'from_lat' in e || 'lat' in e,
      ) as Record<string, number> | undefined;
      if (focusEvent) {
        const lat = focusEvent.lat ?? focusEvent.from_lat;
        const lon = focusEvent.lon ?? focusEvent.from_lon;
        if (lat != null && lon != null) {
          setViewState({ latitude: lat, longitude: lon, zoom: 3.5, pitch: 20, bearing: 0 });
        }
      }

      pushSimulationTurn(turn);
      setStreamingText('');
      setSimStatus('waiting');
    } catch (err) {
      console.error('[Simulation] Error:', err);
      setSimStatus('waiting');
    }
  }, [simulation.scenario, simulation.turns, countryProfiles,
      setSimStatus, setStreamingText, pushSimulationTurn,
      setSimMissiles, setSimTroopMovements, setSimConflictZones, setViewState]);

  // Auto mode: run next turn 4 seconds after status becomes 'waiting'
  useEffect(() => {
    if (!simulation.autoMode || simulation.status !== 'waiting') return;
    if (simulation.turns.length === 0) return;

    autoTimerRef.current = setTimeout(() => {
      // Auto-pick first choice (least escalatory)
      if (simulation.turns.length === 0) return; // re-check inside async gap
      const lastTurn = simulation.turns[simulation.turns.length - 1];
      const autoChoice = lastTurn?.choices[0]?.id ?? 'A';
      void runTurn(autoChoice as 'A' | 'B' | 'C');
    }, 4000);

    return () => {
      if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    };
  }, [simulation.autoMode, simulation.status, simulation.turns, runTurn]);

  // Stop auto mode / abort streaming
  const stop = useCallback(() => {
    abortRef.current = true;
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    setAutoMode(false);
    setSimStatus('waiting');
  }, [setAutoMode, setSimStatus]);

  return { runTurn, stop };
}
